import { getWmsPushPlan } from '@api/mother-order';
import { approveStn, cancelStn, dispatchStn, getStn, pushStnToWms, type Stn } from '@api/warehouse-ops';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import {
    Alert,
    Badge,
    Button,
    Group,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import { ArrowLeft, Warning } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FC } from 'react';
import { useNavigate, useParams } from 'react-router';

import StnStatusBadge from './StnStatusBadge';
import { useStnReceivePath } from './stnReceivePath';
import { stnNextStep } from './useStnActions';

type StnItemRow = NonNullable<Stn['items']>[number];

const StnDetailContainer: FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    // Where this STN is received: a CM site's Receive tab, a store's or warehouse's GRN.
    const { pathFor: receivePathFor, ready: receiveReady } = useStnReceivePath();

    const query = useQuery({
        queryKey: ['stn', id],
        queryFn: () => getStn(id!),
        enabled: !!id,
    });

    const dispatchMutation = useMutation({
        mutationFn: () => dispatchStn(id!),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stn', id] }),
    });

    const approveMutation = useMutation({
        mutationFn: () => approveStn(id!),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stn', id] }),
    });

    const cancelMutation = useMutation({
        mutationFn: () => cancelStn(id!),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stn', id] }),
    });
    const { data: wmsPlan } = useQuery(['wms-push-plan'], getWmsPushPlan, { staleTime: 5 * 60_000 });
    const pushWmsMutation = useMutation({
        mutationFn: () => pushStnToWms(id!),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stn', id] }),
    });

    const stn = query.data;

    const itemColumns: DataSheetColumn<StnItemRow>[] = [
        { key: 'itemId', header: 'Item ID', width: 200, sticky: true },
        {
            key: 'plannedQty',
            header: 'Planned Qty',
            width: 120,
            align: 'right',
            render: (_, row) => row.plannedQty,
        },
        {
            key: 'dispatchedQty',
            header: 'Dispatched',
            width: 120,
            align: 'right',
            render: (_, row) => row.dispatchedQty ?? '—',
        },
        {
            key: 'receivedQty',
            header: 'Received',
            width: 120,
            align: 'right',
            render: (_, row) => row.receivedQty ?? '—',
        },
    ];

    if (query.isLoading || !stn) {
        return <Text size="sm" c="dimmed">Loading...</Text>;
    }

    // The same next step as the side panel and the Stock Transfers grid.
    const receivePath = receiveReady ? receivePathFor(stn) : null;
    const nextStep = stnNextStep(stn, !!receivePath);

    const dispatchWarnings = (stn as any).warnings as Array<{ itemId: string; available: number; requested: number }> | undefined;

    return (
        <Stack gap="md">
            <Group>
                <Button
                    variant="subtle"
                    size="xs"
                    leftSection={<ArrowLeft size={14} />}
                    onClick={() => navigate('/stock-transfers')}
                >
                    Back
                </Button>
            </Group>

            <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                    <Group gap="sm">
                        <Title order={4}>{stn.code}</Title>
                        <StnStatusBadge status={stn.status} />
                    </Group>
                    <Text size="sm" c="dimmed">
                        {stn.fromNodeId} → {stn.toNodeId}
                    </Text>
                    <Text size="xs" c="dimmed">
                        {stn.itemType === 'product' ? 'Finished Goods' : 'Raw / Packaging Material'}
                        {stn.workOrderId ? ` · WO: ${stn.workOrderId}` : ''}
                        {stn.createdBy ? ` · Created by ${stn.createdBy}` : ''}
                    </Text>
                    {stn.wmsGatePassCode && (
                        <Text size="xs" c="dimmed">WMS gate pass: {stn.wmsGatePassCode}</Text>
                    )}
                </Stack>
                <Group gap="xs">
                    {wmsPlan?.stn && !stn.wmsGatePassCode && stn.status !== 'CANCELLED' && (
                        <Button
                            size="xs"
                            variant="default"
                            loading={pushWmsMutation.isPending}
                            onClick={() => pushWmsMutation.mutate()}
                            title="Create this STN in the WMS as a gate pass."
                        >
                            Push to WMS
                        </Button>
                    )}
                    {(stn.status === 'DRAFT' || stn.status === 'APPROVED') && (
                        <Button
                            size="xs"
                            color="red"
                            variant="subtle"
                            loading={cancelMutation.isPending}
                            onClick={() => cancelMutation.mutate()}
                        >
                            Cancel
                        </Button>
                    )}
                    {nextStep === 'APPROVE' && (
                        <Button
                            size="xs"
                            loading={approveMutation.isPending}
                            onClick={() => approveMutation.mutate()}
                        >
                            Approve
                        </Button>
                    )}
                    {nextStep === 'DISPATCH' && (
                        <Button
                            size="xs"
                            loading={dispatchMutation.isPending}
                            onClick={() => dispatchMutation.mutate()}
                        >
                            Dispatch
                        </Button>
                    )}
                    {/* Received on its page; a channel, customer or distributor receives on their side. */}
                    {nextStep === 'RECEIVE' && receivePath && (
                        <Button size="xs" onClick={() => void navigate(receivePath)}>
                            Confirm Receipt
                        </Button>
                    )}
                </Group>
            </Group>

            {dispatchWarnings && dispatchWarnings.length > 0 && (
                <Alert icon={<Warning size={16} />} color="yellow" title="Stock balance warnings">
                    {dispatchWarnings.map((w) => (
                        <Text key={w.itemId} size="sm">
                            {w.itemId}: requested {w.requested}, available {w.available}
                        </Text>
                    ))}
                </Alert>
            )}

            <DataSheet<StnItemRow>
                title="Items"
                data={stn.items ?? []}
                columns={itemColumns}
                getRowKey={(row) => row.id}
                hideHeaderBar={false}
                maxHeight="auto"
            />

            {stn.status === 'RECEIVED' && (
                <Text size="sm" c="dimmed">
                    Received at {stn.receivedAt ? new Date(stn.receivedAt).toLocaleString() : '—'}.
                    Create a GRN at the destination warehouse to process stock.
                </Text>
            )}

        </Stack>
    );
};

export default StnDetailContainer;
